import type { ComboOption } from '../components/Combo';
import type { Store } from '../data/stores';
import { colors } from '../styles/tokens';

/**
 * Tienda default de la vista Mi tienda cuando no llega `miTiendaStoreId` desde
 * el drill de la vista Global: `MARCHA#59` si existe en el catálogo (línea
 * 274 del handoff — `S.find(s=>s.id==='MARCHA#59') ? 'MARCHA#59' : S[0].id`),
 * si no la primera tienda del catálogo.
 */
export const MI_TIENDA_DEFAULT_ID = 'MARCHA#59';

function opt(value: string, label: string): ComboOption {
  return { value, label };
}

/**
 * Resuelve el id de tienda default de Mi tienda (línea 274 del handoff).
 * `null` si el catálogo viene vacío (aún no cargó).
 */
export function defaultStoreId(stores: Store[]): string | null {
  if (stores.some((s) => s.id === MI_TIENDA_DEFAULT_ID)) {
    return MI_TIENDA_DEFAULT_ID;
  }
  return stores[0]?.id ?? null;
}

export interface MiTiendaOptions {
  miRegionalOpts: ComboOption[];
  miSupervisorOpts: ComboOption[];
  miTiendaOpts: ComboOption[];
}

/**
 * Pools de opciones de los 3 `Combo` del drill Mi tienda (líneas 402-404 del
 * handoff). `store` (la tienda seleccionada) es la ÚNICA fuente de verdad:
 * `miRegionalOpts` son los regionales distintos del catálogo;
 * `miSupervisorOpts` son los supervisores distintos de tiendas en
 * `store.regional`; `miTiendaOpts` son las tiendas en (`store.regional` +
 * `store.supervisor`).
 *
 * ⚠️ El Regional estuvo cableado a dos nombres hasta el 12-oct, el mismo
 * defecto que Global ya había corregido en `computeFilterOptions`: un regional
 * nuevo o renombrado no aparecía y sus tiendas sólo se alcanzaban por el combo
 * de Tienda. El regional no existe en Proscai; este catálogo es su única fuente.
 */
export function computeMiTiendaOptions(stores: Store[], store: Store): MiTiendaOptions {
  const miRegionalOpts: ComboOption[] = Array.from(new Set(stores.map((s) => s.regional)))
    .filter(Boolean)
    .sort()
    .map((r) => opt(r, r));

  const miSupervisorOpts: ComboOption[] = Array.from(
    new Set(stores.filter((s) => s.regional === store.regional).map((s) => s.supervisor)),
  )
    .sort()
    .map((x) => opt(x, x));

  const miTiendaOpts: ComboOption[] = stores
    .filter((s) => s.regional === store.regional && s.supervisor === store.supervisor)
    .map((s) => opt(s.id, `${s.tienda} · ${s.cadena}`));

  return { miRegionalOpts, miSupervisorOpts, miTiendaOpts };
}

/**
 * `setMiRegional` (línea 344 del handoff): elegir un Regional salta a la
 * PRIMERA tienda de ese regional (en orden de catálogo) — el regional en sí
 * no es un campo de estado independiente, se deriva de la tienda resultante.
 * `null` si el regional no tiene ninguna tienda (catálogo corrupto).
 */
export function firstStoreOfRegional(stores: Store[], regional: string): Store | null {
  return stores.find((s) => s.regional === regional) ?? null;
}

/**
 * `setMiSupervisor` (línea 345 del handoff): elegir un Supervisor salta a la
 * PRIMERA tienda del regional ACTUAL (el de la tienda ya seleccionada) que
 * tenga ese supervisor. `null` si no hay ninguna coincidencia.
 */
export function firstStoreOfSupervisor(
  stores: Store[],
  regional: string,
  supervisor: string,
): Store | null {
  return stores.find((s) => s.regional === regional && s.supervisor === supervisor) ?? null;
}

/**
 * Delta relativo `(hoy-ayer)/ayer` para las KPI cards "hoy vs ayer" de Mi
 * tienda (líneas 407-417 del handoff, adaptado a datos reales — el handoff
 * generaba un delta aleatorio; acá se calcula de los valores reales de la
 * API). Devuelve una FRACCIÓN (no 0..100): formatear con `fmtPct` como el
 * resto del tablero.
 *
 * Null-safe: `null` cuando no hay denominador válido (`ayer` es `null`,
 * `undefined`, `NaN` o `0`) o cuando `hoy` no es un número válido — evita
 * mostrar `Infinity%`/`NaN%` en vez de "sin delta".
 */
export function computeDelta(
  hoy: number | null | undefined,
  ayer: number | null | undefined,
): number | null {
  if (
    hoy === null ||
    hoy === undefined ||
    Number.isNaN(hoy) ||
    ayer === null ||
    ayer === undefined ||
    Number.isNaN(ayer) ||
    ayer === 0
  ) {
    return null;
  }
  return (hoy - ayer) / ayer;
}

/**
 * Color del delta "hoy vs ayer" para las KPI cards (`KpisHoy`): verde cuando
 * el cambio es una mejora y rojo cuando es un empeoramiento; el tono neutro
 * (`texto.quaternary`) cuando no hay delta que mostrar (`null`).
 *
 * Por default ("subir es bueno"), un delta positivo -> verde y uno negativo
 * -> rojo. Para KPIs donde subir es MALO (Negados: más piezas negadas es
 * peor, no mejor), pasar `invert: true` para invertir el mapeo (subir ->
 * rojo, bajar -> verde) SIN tocar el signo del propio delta — el texto/
 * flecha (`▲`/`▼`) de `KpisHoy` siguen reflejando la dirección REAL del
 * cambio; solo el color cambia de sentido.
 */
export function computeDeltaColor(delta: number | null, invert = false): string {
  if (delta === null) {
    return colors.texto.quaternary;
  }
  const isRise = delta >= 0;
  const isImprovement = invert ? !isRise : isRise;
  return isImprovement ? colors.verde : colors.rojo;
}

/**
 * Formatea `lastRefresh` (ISO string tal como lo serializa el backend, o
 * `null`) como hora LOCAL `HH:MM` para el label "actualizado" del drill.
 * `'—'` si no hay dato o el string no es una fecha válida.
 */
export function formatLastRefresh(iso: string | null | undefined): string {
  if (!iso) {
    return '—';
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return '—';
  }
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}
